/**
 * Fee schedule — what a store's platform fee is SUPPOSED to be, so engine rule R8 has something
 * to compare a real settlement line against.
 *
 * WHY a seller fills this in at all: until this table has a row for a `line_kind`, R8 has
 * nothing to compare a line to and stays silent — a fee mismatch on a store with no schedule is
 * not "clean", it is simply unchecked. Filling in a rate here is what turns the rule on.
 *
 * WHY history is never overwritten: `POST` closes the previous row's `effective_to` and opens a
 * new one rather than editing it in place (it returns the closed row's id as `superseded_id`,
 * which is exactly why this screen does not need a second request to show it) — a line settled
 * last month is still judged by the rate actually in force on that day, not by whatever the
 * seller enters today.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { api, type FeeSchedule as FeeScheduleRow } from "../api";
import { Amount } from "../components/Amount";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { formatDate } from "../format";
import {
  bpsToPercentString, feeScheduleSections, sectionLabel, validateFeeScheduleForm,
  type FeeScheduleFormErrors, type FeeScheduleSection,
} from "../fee-schedule";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

/** Most PayoutAudit sellers are US SMBs (CLAUDE.md); a store with no synced currency yet still
 *  needs one to fill the form in, so this is only a starting point, not a claim about the store. */
const FALLBACK_CURRENCY = "USD";

const SALE_HINT = "The normal choice — the fee the platform charges on an ordinary order.";
const REFUND_HINT =
  "Platforms typically KEEP the processing fee on a refund, so a real refund line carries no fee and needs no " +
  "schedule (a kept fee shows on the Payouts screen as a named loss, not a discrepancy). Only set a refund " +
  "schedule if your platform actually returns the fee.";

/**
 * A section for one platform charge rather than for a whole `line_kind`, because a kind can be a
 * bucket: Amazon's PLATFORM_FEE holds the flat monthly Subscription next to a variable
 * FBAInventoryFee, and one rate cannot describe both.
 */
function sectionHint(section: FeeScheduleSection): string {
  if (section.nativeSubtype) {
    return `A charge your platform makes by this name, priced on its own. Other ${section.lineKind} charges are ` +
      "unaffected — a rate set here applies only to lines the platform calls " + `"${section.nativeSubtype}".`;
  }
  if (section.lineKind === "SALE") return SALE_HINT;
  if (section.lineKind === "REFUND") return REFUND_HINT;
  return `Every ${section.lineKind} line on this store that no more specific rate covers.`;
}

export function FeeSchedule(): ReactNode {
  const store = useStore();
  // Both, because the sections are derived from the store's own fee-bearing lines: without the
  // summary this screen could not offer to price a charge the seller can plainly see is unchecked
  // on the overview, which is where the prompt to come here comes from.
  const state = useAsync(
    async () => {
      const [schedule, connection] = await Promise.all([api.feeSchedule(store.id), api.connection(store.id)]);
      return { schedules: schedule.schedules, coverage: connection.summary.fee_coverage };
    },
    [store.id],
  );

  if (state.status === "loading") return <Loading label="Loading fee schedule" />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={state.reload} />;

  const { schedules, coverage } = state.data;
  const currency = store.default_currency ?? FALLBACK_CURRENCY;
  const sections = feeScheduleSections(coverage, schedules);

  return (
    <>
      <header className="page-head">
        <h1 className="page-title">Fees</h1>
      </header>

      <div className="notice notice-info">
        <p className="notice-title">What setting a rate does</p>
        <p className="notice-text">
          Reconciliation rule R8 compares every settlement line against the schedule that was in force on the day it
          was cut, and raises a WARN on a mismatch. A new rate does not rewrite history: the old one keeps its date
          range, so a line from before today is still judged by the rate that applied then.
        </p>
      </div>

      {sections.map((section) => (
        <LineKindSection
          key={`${section.lineKind}:${section.nativeSubtype ?? ""}`}
          storeId={store.id}
          section={section}
          rows={schedules.filter((s) => s.line_kind === section.lineKind && s.native_subtype === section.nativeSubtype)}
          currency={currency}
          onCreated={state.reload}
        />
      ))}
    </>
  );
}

function LineKindSection({ storeId, section, rows, currency, onCreated }: {
  storeId: string;
  section: FeeScheduleSection;
  rows: FeeScheduleRow[];
  currency: string;
  onCreated: () => void;
}): ReactNode {
  const current = rows.find((r) => r.effective_to === null) ?? null;
  // Newest first: the point of this list is "what used to apply", read top-down from most recent.
  const history = [...rows.filter((r) => r.effective_to !== null)].sort((a, b) => b.effective_from.localeCompare(a.effective_from));
  const label = sectionLabel(section);

  return (
    <section className="card">
      <h2 className="card-title">{label} fee</h2>
      <p className="state-hint">{sectionHint(section)}</p>

      {current ? (
        <dl className="kv">
          <div><dt>Rate</dt><dd>{bpsToPercentString(current.rate_bps)}%</dd></div>
          <div><dt>Flat</dt><dd><Amount minor={current.flat_minor} currency={current.currency} /></dd></div>
          <div><dt>In effect since</dt><dd>{formatDate(current.effective_from)}</dd></div>
          <div><dt>Source</dt><dd>{current.source}</dd></div>
        </dl>
      ) : (
        <EmptyState
          title="No schedule set"
          hint={`R8 does not check ${label.toLowerCase()} lines for this store until a rate is entered below.`}
        />
      )}

      {history.length > 0 ? (
        <div className="table-wrap kv-dense">
          <table className="table table-inner">
            <thead>
              <tr><th>Rate</th><th>Flat</th><th>Effective</th><th>Source</th></tr>
            </thead>
            <tbody>
              {history.map((row) => (
                // De-emphasised, not hidden: an ended row is the context a seller needs to trust
                // that a past line was judged fairly, not clutter left over from an old mistake.
                <tr key={row.id} className="muted">
                  <td>{bpsToPercentString(row.rate_bps)}%</td>
                  <td><Amount minor={row.flat_minor} currency={row.currency} /></td>
                  <td>{formatDate(row.effective_from)} – {formatDate(row.effective_to)}</td>
                  <td>{row.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <NewScheduleForm storeId={storeId} section={section} label={label} currency={currency} onCreated={onCreated} />
    </section>
  );
}

function NewScheduleForm({ storeId, section, label, currency, onCreated }: {
  storeId: string;
  section: FeeScheduleSection;
  label: string;
  currency: string;
  onCreated: () => void;
}): ReactNode {
  const [ratePercent, setRatePercent] = useState("");
  const [flatDollars, setFlatDollars] = useState("");
  const [currencyCode, setCurrencyCode] = useState(currency);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [source, setSource] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FeeScheduleFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const result = validateFeeScheduleForm({
      lineKind: section.lineKind, nativeSubtype: section.nativeSubtype,
      ratePercent, flatDollars, currency: currencyCode, effectiveFrom, source,
    });
    if (!result.ok) {
      setFieldErrors(result.errors);
      return;
    }
    setFieldErrors({});
    setSubmitError(null);
    setSubmitting(true);
    // The server is the authority: browser validation above catches the common typo before a
    // round trip, but a 400 here still wins — it is never assumed to agree with `result.ok`.
    api.createFeeSchedule(storeId, result.value).then(
      () => {
        setSubmitting(false);
        setRatePercent("");
        setFlatDollars("");
        setEffectiveFrom("");
        setSource("");
        onCreated();
      },
      (err: unknown) => {
        setSubmitting(false);
        setSubmitError(err instanceof Error ? err : new Error(String(err)));
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <h3 className="card-title">Set a new {label.toLowerCase()} rate</h3>

      <label className="field">
        <span className="field-label">Rate (percent)</span>
        <input
          className="input"
          type="text"
          inputMode="decimal"
          placeholder="2.9"
          value={ratePercent}
          onChange={(e) => setRatePercent(e.target.value)}
          disabled={submitting}
        />
        {fieldErrors.ratePercent ? <p className="form-error">{fieldErrors.ratePercent}</p> : null}
      </label>

      <label className="field">
        <span className="field-label">Flat fee (dollars)</span>
        <input
          className="input"
          type="text"
          inputMode="decimal"
          placeholder="0.30"
          value={flatDollars}
          onChange={(e) => setFlatDollars(e.target.value)}
          disabled={submitting}
        />
        {fieldErrors.flatDollars ? <p className="form-error">{fieldErrors.flatDollars}</p> : null}
      </label>

      <label className="field">
        <span className="field-label">Currency</span>
        <input
          className="input"
          type="text"
          maxLength={3}
          value={currencyCode}
          onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
          disabled={submitting}
        />
        {fieldErrors.currency ? <p className="form-error">{fieldErrors.currency}</p> : null}
      </label>

      <label className="field">
        <span className="field-label">Effective from</span>
        <input
          className="input"
          type="date"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          disabled={submitting}
        />
        {fieldErrors.effectiveFrom ? <p className="form-error">{fieldErrors.effectiveFrom}</p> : null}
      </label>

      <label className="field">
        <span className="field-label">Source (where this number came from)</span>
        <input
          className="input"
          type="text"
          placeholder="Shopify Basic US plan"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          disabled={submitting}
        />
        {fieldErrors.source ? <p className="form-error">{fieldErrors.source}</p> : null}
      </label>

      <div className="actions">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Saving…" : "Save rate"}
        </button>
      </div>
      {submitError ? <p className="form-error">{submitError.message}</p> : null}
    </form>
  );
}
